import moment from "moment-timezone";

import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useLocation from "../hooks/useLocation";
import useEvent from "../hooks/useEvent";
import useTags from "../hooks/useTags";
import { useAuth } from "../hooks/useAuth";

import { ToastContext } from "../Components/ToastProvider";
import TimePicker from "../Components/TimePicker";
import LoadingButton from "../Components/LoadingButton";
import CompleteProfileModal from "./CompleteProfileModal";


import "../App.css";

function CreateEvent() {
  const { id } = useParams();
  const { createToast } = useContext(ToastContext);

  const { profile } = useAuth();
  
  const { location, error } = useLocation(id);
  const { createEvent, errorEvent } = useEvent(null, true);
  const { tags } = useTags();

  const twoDays = moment().add(2, "days");
  const twoDaysFormatted = twoDays.format("YYYY-MM-DD");

  const twoMonths = moment().add(2, "months");
  const twoMonthsFormatted = twoMonths.format("YYYY-MM-DD");

  const [disabled, setDisabled] = useState(false);

  const navigate = useNavigate();

  const [tagsSelected, setTagsSelected] = useState([]);
  const [description, setDescription] = useState("");
  const [chosenDish, setChosenDish] = useState();
  const [date, setDate] = useState(twoDaysFormatted);
  const [time, setTime] = useState(moment("12:30", "HH:mm"));
  const [title, setTitle] = useState("");
  const [languages, setLanguages] = useState("English");
  const [isCompleteProfileModalOpen, toggleIsCompleteProfileModalOpen] = useState(false);


  const setToLocationTimezone = (dateTime, timezone) => {
    const preserveSameTime = true;
    return moment(dateTime).tz(location.city.timezone, preserveSameTime);
  };

  const submitEvent = ({commandFromCompleteProfileModal}) => {
    let showProfileFields = false;
    if (!profile.favorite_food || !profile.gender || !profile.spice_level || !profile.ask_me_about) {
      showProfileFields = true;
    }

    if (showProfileFields && !commandFromCompleteProfileModal) {
      toggleIsCompleteProfileModalOpen(true);
      return;
    }    

    setDisabled(true);

    const dateTime = `${date}T${moment(time).format("HH:mm:ss")}`;

    const dateLocationTimezone = setToLocationTimezone(
      dateTime,
      location.city.timezone
    );

    const utcDate = moment(dateLocationTimezone).utc();
    const utcDateStr = utcDate.toISOString();

    const data = {
      location_id: location.id,
      title: title,
      body: description,
      date: utcDateStr,
      dish_id: chosenDish,
      tag_ids: tagsSelected,
      languages: languages,
    };

    createEvent(data).then(async (id) => {
      createToast("You created an event! 🎆", "success", true);
      navigate(`../../events/${id}`, { replace: true });
    });
  }

  const handleSubmit = (event) => {
    event.preventDefault();

    submitEvent({});
  };

  // Need this for our time picker
  function range(start, end) {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  }

  const disabledTime = () => {
    // Disable times before 8:00 AM and after 11:45 PM
    return { disabledHours: () => range(0, 24).splice(0, 8) };
  };

  function handleCheckboxChanged(e) {
    const val = parseInt(e.target.value);
    if (e.target.checked) {
      setTagsSelected([...tagsSelected, val]);
    } else {
      const arr = [...tagsSelected];
      const index = tagsSelected.indexOf(val);
      arr.splice(index, 1);
      setTagsSelected(arr);
    }
  }

  return (
    <>
      {error && <p className="error-message">Something went wrong...</p>}
      {errorEvent && <p className="error-message">Something went wrong...</p>}
      <h1>Create Your Event</h1>
      <form onSubmit={handleSubmit} className="create-form">
        <label htmlFor="title">Your Event's Name</label>
        <input
          id="title"
          type="text"
          required
          placeholder="My Awesome Event"
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        ></input>
        {location ? (
          <>
            <label htmlFor="location">Event's Location</label>
            <input
              id="location"
              type="text"
              disabled={true}
              value={location.title}
            ></input>
          </>
        ) : (
          <div>Loading...</div>
        )}

        <label htmlFor="description">Event's Description</label>
        <textarea
          id="description"
          value={description}
          required
          rows="10"
          placeholder="Just a little extra flavor. What’s this meetup all about?"
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        ></textarea>

        <label htmlFor="languages">Language(s) I speak</label>
        <input
          id="languages"
          className="capitalize"
          type="text"
          required
          // placeholder="English"
          value={languages}
          onChange={(event) => {
            setLanguages(event.target.value);
          }}
        ></input>

        {location?.dishes.length > 0 && (
          <fieldset>
            <legend>What would you like to try?</legend>
            <p className="remark centered">
              ✨ Choose one dish or drink to feature at your meetup. <br />
              It gives the group a shared starting point, but it’s not a rule.{" "}
              <br />
              Guests can order their own meals or drinks, share, or not. Totally
              up to them.
            </p>
            {location.dishes.map((dish) => (
              <div key={dish.id}>
                <input
                  type="radio"
                  name="chosen-dish"
                  required
                  value={dish.title}
                  checked={chosenDish === dish.id}
                  onChange={(e) => {
                    setChosenDish(dish.id);
                  }}
                ></input>
                <label htmlFor={dish.title} className="radio-label">
                  {dish.title}
                </label>
              </div>
            ))}
          </fieldset>
        )}
        <fieldset>
          <legend>Select tags for your event:</legend>
          {tags?.map((tag) => (
            <div key={tag.id}>
              <input
                type="checkbox"
                value={tag.id}
                checked={tagsSelected.indexOf(tag.id) > -1}
                onClick={handleCheckboxChanged}
              />
              <label htmlFor={tag.title}>{tag.title}</label>
            </div>
          ))}
        </fieldset>
        <fieldset>
          <legend>Select the Date for your Event:</legend>
          <input
            id="date"
            value={date}
            type="date"
            name="date"
            aria-label="date"
            required
            min={twoDaysFormatted}
            max={twoMonthsFormatted}
            onChange={(event) => {
              setDate(event.target.value);
            }}
          />
        </fieldset>
        <fieldset>
          <legend>Select the Time for your Event:</legend>
          <TimePicker
            id="time"
            required
            aria-label="time"
            minuteStep={15}
            format="HH:mm"
            showNow={false}
            disabledTime={disabledTime}
            value={time}
            onChange={(t) => {
              setTime(t);
            }}
          ></TimePicker>
          <p className="margin-b-10 remark">
            Timezone: {location?.city.timezone}
          </p>
        </fieldset>
        <div className="button-wrapper">
          <LoadingButton
            type="submit"
            disabled={disabled}
            color="primary"
            variant="contained"
          >
            Create Your Event
          </LoadingButton>
        </div>
      </form>
      <CompleteProfileModal isOpen={isCompleteProfileModalOpen} toggleOpen={toggleIsCompleteProfileModalOpen} onSave={submitEvent} />
    </>
  );
}

export default CreateEvent;
